import React, { useState, useMemo } from 'react'
import classNames from 'classnames'
import { useQuery } from 'react-query'
import { Button, Loader } from '@crossfit/ui'

import parse from 'date-fns/parse'
import isThisWeek from 'date-fns/isThisWeek'
import isFuture from 'date-fns/isFuture'

import { Tabs, Tab } from 'components/Tabs'
import InnerContent from 'components/InnerContent'
import PageHeader from 'components/PageHeader'
import downloadFile from 'utilities/download-file'
import { fetchPDF } from 'services/programming'
import { fetchPage } from 'services/content'
import { List, ListGroup, ListItem, ListTitle } from './Lists'
import style from './style.module.css'

type PDF = {
  startDate: string
  title: string
  isCurrent?: boolean
  isUpcoming?: boolean
  contentId: string
}

type Track = {
  pdfs: PDF[]
  title: string
}

type ProgrammingContent = {
  previousWeeksLabelText: string
  theLatestLabelText: string
  title: string
  currentText: string
  upcomingText: string
  tracks: Track[]
}

function PDFsPage() {
  const { isLoading, data } = useQuery('programming-pdf-page', () =>
    fetchPage<ProgrammingContent>('/programming/programming-pdf-page'),
  )
  const [selectedTrackIndex, setSelectedTrackIndex] = useState(0)

  const tracks = useMemo(() => {
    if (!data) {
      return []
    }

    return data.tracks.map((track) => {
      const { pdfs, ...rest } = track

      const groups = pdfs
        .sort((a, b) => (a.startDate < b.startDate ? 1 : -1))
        .reduce(
          (acc, pdf) => {
            const { startDate } = pdf
            const date = parse(startDate, 'yyyyLLdd', new Date())

            if (isThisWeek(date)) {
              acc[0].pdfs.push({ ...pdf, isCurrent: true })
            } else if (isFuture(date)) {
              acc[0].pdfs.push({ ...pdf, isUpcoming: true })
            } else {
              acc[1].pdfs.push(pdf)
            }

            return acc
          },
          [
            { title: data.theLatestLabelText, pdfs: [] as PDF[] },
            { title: data.previousWeeksLabelText, pdfs: [] as PDF[] },
          ],
        )

      return {
        groups,
        ...rest,
      }
    })
  }, [data])

  if (isLoading) {
    return <Loader />
  }

  async function handleDownload(pdfURL: string) {
    const blob = await fetchPDF(pdfURL)
    downloadFile(pdfURL, blob)
  }

  return (
    <>
      <PageHeader>{data.title}</PageHeader>
      <InnerContent as='main' variant='large'>
        {tracks.length > 1 && (
          <Tabs>
            {tracks.map((track, index) => (
              <Tab
                key={track.title}
                isActive={selectedTrackIndex === index}
                onClick={() => setSelectedTrackIndex(index)}
                mb={6}
              >
                {track.title}
              </Tab>
            ))}
          </Tabs>
        )}
        {tracks[selectedTrackIndex].groups.map((group) => (
          <ListGroup key={group.title}>
            <ListTitle>{group.title}</ListTitle>
            <List>
              {group.pdfs.map((pdf) => (
                <ListItem key={pdf.title} isCurrent={pdf.isCurrent}>
                  <div className={style.workoutTitle}>
                    {pdf.isCurrent && (
                      <>
                        <div
                          className={classNames(
                            style.workoutLabel,
                            style.isCurrent,
                          )}
                        >
                          {data.currentText}
                        </div>{' '}
                        /{' '}
                      </>
                    )}
                    {pdf.isUpcoming && (
                      <>
                        <div className={style.workoutLabel}>
                          {data.upcomingText}
                        </div>{' '}
                        /{' '}
                      </>
                    )}
                    {pdf.title}
                  </div>
                  <Button onClick={() => handleDownload(pdf.contentId)}>
                    Download
                  </Button>
                </ListItem>
              ))}
            </List>
          </ListGroup>
        ))}
      </InnerContent>
    </>
  )
}

export default PDFsPage
